import * as React from 'react';
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Flex, Switch, theme, Tooltip, Typography } from 'antd';
import { useGetScreenSize } from '../../../common/hooks/useGetScreenSize';
import { useAppStore } from '../../../common/store';
import { t } from '../../../utils';
import { isAccountKBAEnabled } from '../../../utils/access';

const { Title, Text } = Typography;

export const SecurityOptions = () => {
	const { token } = theme.useToken();
	const { documentTemplate, updateDocumentTemplate, account } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		updateDocumentTemplate: state.updateDocumentTemplate,
		account: state.account,
	}));

	const toggleSecurityOption = (option: 'isKbaEnabled' | 'isPasscodeEnabled') => {
		const availableOption =
			option === 'isKbaEnabled' ? 'isPasscodeEnabled' : 'isKbaEnabled';
		const newSettings = {
			[option]: !documentTemplate?.[option],
			[availableOption]: false,
		};
		updateDocumentTemplate(newSettings);
	};

	return (
		<>
			<Title level={5}>{t('reviewPage.securityOptionsLabel')}</Title>
			<Flex
				style={{
					flexDirection: 'column',
					paddingTop: token.paddingXS,
				}}
				gap={token.marginLG}
			>
				{isAccountKBAEnabled(account) && (
					<SettingsField
						isChecked={documentTemplate?.isKbaEnabled || false}
						onChange={() => toggleSecurityOption('isKbaEnabled')}
						optionText={t('reviewPage.kbaRequirement')}
						optionTooltipText={t('reviewPage.kbaTooltip')}
					/>
				)}
				<SettingsField
					isChecked={documentTemplate?.isPasscodeEnabled || false}
					onChange={() => toggleSecurityOption('isPasscodeEnabled')}
					optionText={t('reviewPage.passcodeLabel')}
					optionTooltipText={t('reviewPage.passCodeTooltip')}
				/>
			</Flex>
		</>
	);
};

type SettingsField = {
	isChecked: boolean;
	onChange: () => void;
	optionText: string;
	optionTooltipText?: string;
};

const SettingsField = ({
	isChecked,
	onChange,
	optionText,
	optionTooltipText,
}: SettingsField) => {
	const { isExtraExtraLarge, isExtraSmallScreen, isSmallScreen, isMediumScreen } =
		useGetScreenSize();
	const { token } = theme.useToken();
	let gap = 4;
	if (isExtraExtraLarge) {
		gap = 0;
	}
	if (isExtraSmallScreen || isSmallScreen || isMediumScreen) {
		gap = 8;
	}

	return (
		<Flex
			style={{
				justifyContent: 'center',
				alignItems: 'center',
			}}
			gap={gap}
		>
			<Flex
				style={{
					flex: 1,
				}}
			>
				<Switch
					checked={isChecked}
					onChange={onChange}
					checkedChildren={<CheckOutlined />}
					unCheckedChildren={<CloseOutlined />}
					aria-label={optionText}
				/>
			</Flex>
			<Flex
				style={{
					flex: 9,
				}}
			>
				<Text strong>{optionText}</Text>
			</Flex>
			{optionTooltipText && (
				<Flex
					style={{
						flex: 1,
						justifyContent: 'end',
					}}
				>
					<Tooltip
						placement="right"
						title={optionTooltipText}
						trigger={['hover', 'focus']}
					>
						<QuestionCircleOutlined
							style={{ fontSize: token.sizeMD }}
							tabIndex={0}
							aria-label={optionTooltipText}
						/>
					</Tooltip>
				</Flex>
			)}
		</Flex>
	);
};
